import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTestTemplateColumns } from "../hooks"
import { useTestTemplatesSearchStore } from "../stores"

import TestTemplateFormDialog from "./TestTemplateForm/TestTemplateFormDialog"
import TestTemplatesTable from "./TestTemplatesTable/TestTemplatesTable"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import { PermissionActionType } from "@/constants/permissions"
import type { TestTemplateSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const TestTemplatesContainer: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/admin/manage-products/_layout/test-templates" })
  const { query, field, order, ...restSearchParams } = currentSearch
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const { hasPermissions } = usePermissionManager()

  const data = useTestTemplatesSearchStore(({ data }) => data)
  const fetching = useTestTemplatesSearchStore(({ fetching }) => fetching)
  const error = useTestTemplatesSearchStore(({ error }) => error)
  const executeSearch = useTestTemplatesSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useTestTemplatesSearchStore(({ loadMore }) => loadMore)
  const clearStore = useTestTemplatesSearchStore(({ clearStore }) => clearStore)
  const refetch = useCallback(() => executeSearch(queryClient, currentSearch, true), [currentSearch, executeSearch])

  const columns = useTestTemplateColumns(refetch)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    refetch()
  }, [refetch])

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = upperSnakeCaseToCamelCase(field)

    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id
      const sortField = toUpperSnakeCase(key) as TestTemplateSortField
      const params = {
        ...restSearchParams,
        query,
        field: sortField,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }
      navigate({
        search: getRefinedSearchParams(params).search,
        to: "/app/admin/manage-products/test-templates",
        replace: true,
      })
    }
  }

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, currentSearch)
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleFormDialogOpen = () => setIsFormDialogOpen(true)

  const handleFormDialogSubmit = () => {
    setIsFormDialogOpen(false)
    refetch()
  }

  const handleFormDialogClose = () => setIsFormDialogOpen(false)

  return (
    <Box className='flex h-full flex-col gap-6' data-testid='test-temlates-container'>
      <TableHeaderActions currentSearch={currentSearch} placeholder='Search by Test Number, Description or Product ID'>
        {hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE]) && (
          <Button
            startIcon={<AddOutlined />}
            appearance='outlined'
            className='ml-auto'
            onClick={handleFormDialogOpen}
            data-testid='create-button'
          >
            New Test Template
          </Button>
        )}
      </TableHeaderActions>
      <Box className='flex flex-1 flex-col overflow-y-auto' data-testid='test-templates'>
        <TestTemplatesTable
          data={data}
          fetching={fetching}
          columns={columns}
          sorting={sorting}
          error={error}
          tableContainerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
        />
      </Box>
      {isFormDialogOpen && <TestTemplateFormDialog onSubmit={handleFormDialogSubmit} onClose={handleFormDialogClose} />}
    </Box>
  )
}

export default TestTemplatesContainer
